import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, Minus, Eye, FileText, Edit, Trash2, History } from 'lucide-react';
import { userApi } from '../services/api';
import { User } from '../types';
import PointLogs from './PointLogs';
import UserAnalysisHistoryModal from './UserAnalysisHistoryModal';

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showPointModal, setShowPointModal] = useState(false);
  const [pointAction, setPointAction] = useState<'add' | 'deduct'>('add');
  const [pointAmount, setPointAmount] = useState('');
  const [pointReason, setPointReason] = useState('');
  const [pointLoading, setPointLoading] = useState(false);
  const [showPointLogs, setShowPointLogs] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showAnalysisHistory, setShowAnalysisHistory] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState<{ id: string; nickname: string } | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ userId: string; nickname: string } | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchUsers = useCallback(
    async (page = 1, searchTerm = '', append = false) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
          setUsers([]);
          setPagination((prev) => ({ ...prev, page: 1 }));
        }

        const response = await userApi.getUsers(page, pageSize, searchTerm);
        const newUsers = Array.isArray(response.users) ? response.users : [];

        if (append) {
          setUsers((prev) => [...prev, ...newUsers]);
        } else {
          setUsers(newUsers);
        }

        setPagination((prev) => ({
          ...prev,
          page: page,
          total: response.pagination?.total || (append ? prev.total : newUsers.length),
          pages: Math.ceil((response.pagination?.total || (append ? prev.total : newUsers.length)) / pageSize),
        }));

        setHasMore(newUsers.length === pageSize);
      } catch (err) {
        setError('서버 연결에 실패했습니다.');
        console.error('Users fetch error:', err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [pageSize]
  );

  const loadMoreUsers = useCallback(() => {
    if (!loadingMore && hasMore) {
      const nextPage = pagination.page + 1;
      fetchUsers(nextPage, search, true);
    }
  }, [loadingMore, hasMore, search, pagination.page, fetchUsers]);

  useEffect(() => {
    fetchUsers();
  }, [pageSize, fetchUsers]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setHasMore(true);
    fetchUsers(1, search, false);
  };

  const handleUserClick = async (userId: string) => {
    try {
      const response = await userApi.getUser(userId);
      if (response.success) {
        setSelectedUser(response.data.user);
        setShowUserModal(true);
      }
    } catch (err) {
      console.error('User fetch error:', err);
    }
  };

  const handlePointAction = async () => {
    if (!selectedUser || !pointAmount) return;

    try {
      setPointLoading(true);
      const amount = parseInt(pointAmount);
      const reason = pointReason || '관리자 조정';

      let response;
      if (pointAction === 'add') {
        response = await userApi.addUserPoints(selectedUser.id, amount, reason);
      } else {
        response = await userApi.deductUserPoints(selectedUser.id, amount, reason);
      }

      if (response.success) {
        // 사용자 목록 새로고침
        fetchUsers(pagination.page, search);
        // 선택된 사용자 정보 업데이트
        if (selectedUser) {
          const userResponse = await userApi.getUser(selectedUser.id);
          if (userResponse.success) {
            setSelectedUser(userResponse.data.user);
          }
        }
        setShowPointModal(false);
        setPointAmount('');
        setPointReason('');
      }
    } catch (err) {
      console.error('Point action error:', err);
    } finally {
      setPointLoading(false);
    }
  };

  const handleViewPointLogs = (user: User) => {
    setSelectedUserId(user.id);
    setShowPointLogs(true);
  };

  const handleDeleteUser = async () => {
    if (!deleteConfirm) return;

    try {
      setDeleteLoading(true);
      const response = await userApi.deleteUser(deleteConfirm.userId);

      if (response.success) {
        alert(`사용자 ${deleteConfirm.nickname}이 성공적으로 삭제되었습니다.`);
        setDeleteConfirm(null);
        fetchUsers(pagination.page, search);
      } else {
        alert(response.message || '사용자 삭제에 실패했습니다.');
      }
    } catch (err) {
      alert('사용자 삭제 중 오류가 발생했습니다.');
      console.error('Delete user error:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleShowAnalysisHistory = (userId: string, nickname: string) => {
    setSelectedUserForHistory({ id: userId, nickname });
    setShowAnalysisHistory(true);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Seoul', // 한국 시간대 명시적 지정
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white p-4 rounded-lg shadow">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">사용자 관리</h1>
        <p className="mt-2 text-gray-600">사용자 정보를 조회하고 포인트를 관리하세요</p>
      </div>

      {/* 검색 및 필터 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">사용자 검색</label>
            <form onSubmit={handleSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="닉네임, 이메일, 카카오ID로 검색..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </form>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">페이지당 표시</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setHasMore(true);
                fetchUsers(1, search, false);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10개</option>
              <option value={20}>20개</option>
              <option value={50}>50개</option>
              <option value={100}>100개</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => {
                setSearch('');
                setHasMore(true);
                fetchUsers(1, '', false);
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              초기화
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      {/* 사용자 목록 */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사용자
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  포인트
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  사용량
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  가입일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  마지막 로그인
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">액션</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.profileImage ? (
                          <img className="h-10 w-10 rounded-full" src={user.profileImage} alt={user.nickname} />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <span className="text-gray-600 font-medium">{user.nickname.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.nickname}</div>
                        <div className="text-sm text-gray-500">{user.email || user.kakaoId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.points?.balance?.toLocaleString() || 0}P</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div className="flex flex-col space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">일일:</span>
                          <span className="font-medium text-blue-600">{user.usage?.dailyUsed || 0}회</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">월간:</span>
                          <span className="font-medium text-green-600">{user.usage?.monthlyUsed || 0}회</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      {user.isPremium && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          프리미엄
                        </span>
                      )}
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        활성
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.createdAt)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.lastLoginAt)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleUserClick(user.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="상세보기"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowPointModal(true);
                        }}
                        className="text-green-600 hover:text-green-900"
                        title="포인트 관리"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleShowAnalysisHistory(user.id, user.nickname)}
                        className="text-purple-600 hover:text-purple-900"
                        title="관상 분석 이력"
                      >
                        <History className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ userId: user.id, nickname: user.nickname })}
                        className="text-red-600 hover:text-red-900"
                        title="사용자 삭제"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 더 보기 버튼 및 통계 */}
        <div className="bg-white px-4 py-3 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-gray-700">
                총 <span className="font-medium">{pagination.total}</span>명 중{' '}
                <span className="font-medium">{users.length}</span>명 표시
              </p>
            </div>
            <div>
              {loadingMore && (
                <div className="flex items-center text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>더 불러오는
                  중...
                </div>
              )}
            </div>
          </div>

          {/* 더 보기 버튼 */}
          {hasMore && !loadingMore && (
            <div className="text-center">
              <button
                onClick={loadMoreUsers}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                더 보기
              </button>
            </div>
          )}

          {!hasMore && users.length > 0 && (
            <div className="text-center text-sm text-gray-500 py-2">모든 사용자를 불러왔습니다.</div>
          )}
        </div>
      </div>

      {/* 사용자 상세 모달 */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center mb-4">
                  {selectedUser.profileImage ? (
                    <img
                      className="h-12 w-12 rounded-full"
                      src={selectedUser.profileImage}
                      alt={selectedUser.nickname}
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-600 font-medium text-lg">{selectedUser.nickname.charAt(0)}</span>
                    </div>
                  )}
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">{selectedUser.nickname}</h3>
                    <p className="text-sm text-gray-500">{selectedUser.email || selectedUser.kakaoId}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">성별</label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.gender === 'M' ? '남성' : selectedUser.gender === 'F' ? '여성' : '기타'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">생년월일</label>
                      <p className="text-sm text-gray-900">
                        {selectedUser.birthDate ? formatDate(selectedUser.birthDate) : '-'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-500">포인트 현황</label>
                      <button
                        onClick={() => handleViewPointLogs(selectedUser)}
                        className="flex items-center px-2 py-1 text-xs text-blue-600 bg-blue-50 rounded hover:bg-blue-100 transition-colors"
                      >
                        <FileText className="h-3 w-3 mr-1" />
                        포인트 로그
                      </button>
                    </div>
                    <div className="mt-1 grid grid-cols-3 gap-2 text-sm">
                      <div className="text-center p-2 bg-gray-50 rounded">
                        <div className="font-medium">{selectedUser.points?.balance.toLocaleString() || 0}P</div>
                        <div className="text-gray-500">보유</div>
                      </div>
                      <div className="text-center p-2 bg-green-50 rounded">
                        <div className="font-medium text-green-600">
                          {selectedUser.points?.totalEarned.toLocaleString() || 0}P
                        </div>
                        <div className="text-gray-500">적립</div>
                      </div>
                      <div className="text-center p-2 bg-red-50 rounded">
                        <div className="font-medium text-red-600">
                          {selectedUser.points?.totalSpent.toLocaleString() || 0}P
                        </div>
                        <div className="text-gray-500">사용</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">사용량 (실시간)</label>
                    <div className="mt-1 grid grid-cols-2 gap-3 text-sm">
                      <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-2xl font-bold text-blue-600">{selectedUser.usage?.dailyUsed || 0}</div>
                        <div className="text-gray-600 font-medium">일일 사용량</div>
                        <div className="text-xs text-gray-500 mt-1">오늘 기준</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="text-2xl font-bold text-green-600">{selectedUser.usage?.monthlyUsed || 0}</div>
                        <div className="text-gray-600 font-medium">월간 사용량</div>
                        <div className="text-xs text-gray-500 mt-1">이번 달 기준</div>
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      * 사용량은 관상 분석 기록을 기반으로 실시간 계산됩니다
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={() => setShowUserModal(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  닫기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 포인트 관리 모달 */}
      {showPointModal && selectedUser && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-4">포인트 관리 - {selectedUser.nickname}</h3>
                <div className="mb-4">
                  <p className="text-sm text-gray-500">
                    현재 보유 포인트: {selectedUser.points?.balance?.toLocaleString() || 0}P
                  </p>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">액션 선택</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPointAction('add');
                        }}
                        className={`p-2 text-sm rounded-md border ${
                          pointAction === 'add'
                            ? 'border-green-500 bg-green-50 text-green-700'
                            : 'border-gray-300 text-gray-700'
                        }`}
                      >
                        <Plus className="h-4 w-4 mx-auto mb-1" />
                        추가
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPointAction('deduct');
                        }}
                        className={`p-2 text-sm rounded-md border ${
                          pointAction === 'deduct'
                            ? 'border-red-500 bg-red-50 text-red-700'
                            : 'border-gray-300 text-gray-700'
                        }`}
                      >
                        <Minus className="h-4 w-4 mx-auto mb-1" />
                        차감
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">포인트</label>
                    <input
                      type="number"
                      value={pointAmount}
                      onChange={(e) => setPointAmount(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="포인트 입력"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">사유 (선택사항)</label>
                    <input
                      type="text"
                      value={pointReason}
                      onChange={(e) => setPointReason(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="포인트 조정 사유"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePointAction();
                  }}
                  disabled={!pointAmount || pointLoading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {pointLoading ? '처리중...' : '적용'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowPointModal(false);
                  }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 포인트 로그 모달 */}
      {showPointLogs && selectedUserId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-7xl w-full max-h-[90vh] overflow-hidden">
            <PointLogs userId={selectedUserId} onClose={() => setShowPointLogs(false)} />
          </div>
        </div>
      )}

      {/* 관상 분석 이력 모달 */}
      {showAnalysisHistory && selectedUserForHistory && (
        <UserAnalysisHistoryModal
          isOpen={showAnalysisHistory}
          onClose={() => setShowAnalysisHistory(false)}
          userId={selectedUserForHistory.id}
          userNickname={selectedUserForHistory.nickname}
        />
      )}

      {/* 사용자 삭제 확인 모달 */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900 mb-2">사용자 삭제 확인</h3>
                <p className="text-sm text-gray-500 mb-4">
                  <strong>{deleteConfirm.nickname}</strong> 사용자를 정말 삭제하시겠습니까?
                </p>
                <p className="text-xs text-red-600 mb-6">
                  ⚠️ 이 작업은 되돌릴 수 없으며, 사용자의 모든 데이터(관상 분석 이력, 포인트 거래 내역 등)가 영구적으로
                  삭제됩니다.
                </p>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                onClick={handleDeleteUser}
                disabled={deleteLoading}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? '삭제 중...' : '삭제'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteLoading}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
